/**
 * The TopBar flag (spec section 1). Renders nothing unless the user holds `Submit Feedback` AND the
 * environment's availability route says `enabled: true`; while either is loading it renders nothing,
 * so the flag never flashes in and out. It is hidden, never disabled. There is no keyboard shortcut.
 */
import { Flag } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { activateFlag, hasDraft, useFeedbackState } from './feedbackStore';
import { useCanSubmitFeedback } from './useCanSubmitFeedback';
import { useFeedbackAvailability } from './useFeedbackAvailability';

export function FeedbackFlag() {
  const canSubmit = useCanSubmitFeedback();
  const available = useFeedbackAvailability(canSubmit);
  const state = useFeedbackState();

  if (!canSubmit || !available) return null;

  const mounted = state.view !== 'closed';
  const showDot = hasDraft(state) && state.view !== 'expanded' && state.view !== 'picking';
  const label = showDot ? 'Send feedback (draft in progress)' : 'Send feedback';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={activateFlag}
          aria-label={label}
          aria-haspopup="dialog"
          aria-controls={mounted ? 'feedback-panel' : undefined}
          data-testid="feedback-flag"
          className="relative inline-flex items-center justify-center rounded p-1.5 hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-topbar-fg focus-visible:outline-none"
        >
          <Flag className="size-4" aria-hidden />
          {showDot && (
            <span
              aria-hidden="true"
              data-testid="feedback-flag-dot"
              className="absolute top-0.5 right-0.5 size-2 rounded-full bg-warning ring-2 ring-topbar-fg"
            />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}
